import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Users,
  Download,
  QrCode,
  Printer,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Search,
  Trash2,
  Edit,
} from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fixed bilingual notes for the QR attendance sheet (v1: EN + Simplified Chinese).
// Structured so additional languages can be added later without a rewrite.
const QR_NOTES = {
  heading: { zh: "职业健康与安全考勤二维码", en: "OH&S Attendance QR Code" },
  signin: { zh: "请每天签到", en: "You must sign in daily" },
  help: {
    zh: "如果没有你的公司，或看不懂，请联系现场经理",
    en: "If your company is not listed, or you don't understand, see the site manager",
  },
};

// Helper function to get local date in YYYY-MM-DD format
const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Format mobile number as #### ### ###
const formatMobileNumber = (value) => {
  const numbers = value.replace(/\D/g, "");
  if (numbers.length <= 4) {
    return numbers;
  } else if (numbers.length <= 7) {
    return `${numbers.slice(0, 4)} ${numbers.slice(4)}`;
  } else {
    return `${numbers.slice(0, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7, 10)}`;
  }
};

export default function OHSManagement() {
  const { user } = useContext(AuthContext);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [exportStartDate, setExportStartDate] = useState(getLocalDateString());
  const [exportEndDate, setExportEndDate] = useState(getLocalDateString());
  const [exportType, setExportType] = useState("detailed");
  const [attendance, setAttendance] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showQRLangDialog, setShowQRLangDialog] = useState(false);
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState("all");
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, name}
  const [showAddAttendanceDialog, setShowAddAttendanceDialog] = useState(false);
  const [showEditAttendanceDialog, setShowEditAttendanceDialog] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [siteCompanies, setSiteCompanies] = useState([]);
  const [newAttendance, setNewAttendance] = useState({
    mobile_number: "",
    first_name: "",
    surname: "",
    contractor_company: "",
  });

  const token = localStorage.getItem("token");

  // Ensure date is always current when component mounts
  useEffect(() => {
    setSelectedDate(getLocalDateString());
  }, []);

  // Load sites
  useEffect(() => {
    const loadSites = async () => {
      try {
        const response = await fetch(`${API}/sites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSites(data);
          if (data.length > 0) {
            setSelectedSite(data[0].id);
          }
        }
      } catch (error) {
        console.error("Error loading sites:", error);
      }
    };

    loadSites();
  }, [token]);

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const response = await fetch(`${API}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          const companyMap = {};
          data.forEach((company) => {
            companyMap[company.id] = company.name;
          });
          setCompanies(companyMap);
        }
      } catch (error) {
        console.error("Error loading companies:", error);
      }
    };

    loadCompanies();
  }, [token]);

  // Load attendance and comparison data
  useEffect(() => {
    if (selectedSite && selectedDate) {
      loadAttendance();
      loadComparison();
    }
  }, [selectedSite, selectedDate]);

  const loadAttendance = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/ohs/attendance?site_id=${selectedSite}&start_date=${selectedDate}&end_date=${selectedDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAttendance(data);
      }
    } catch (error) {
      console.error("Error loading attendance:", error);
      toast.error("Failed to load attendance records");
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async () => {
    try {
      const response = await fetch(
        `${API}/ohs/attendance/comparison?site_id=${selectedSite}&date=${selectedDate}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setComparison(data);
      }
    } catch (error) {
      console.error("Error loading comparison:", error);
    }
  };

  const handleExport = async () => {
    if (!selectedSite) {
      toast.error("Please select a site");
      return;
    }

    if (!exportStartDate || !exportEndDate) {
      toast.error("Please select date range for export");
      return;
    }

    if (new Date(exportStartDate) > new Date(exportEndDate)) {
      toast.error("Start date must be before or equal to end date");
      return;
    }

    try {
      const isSummary = exportType === "summary";
      const endpoint = isSummary
        ? `${API}/ohs/attendance/summary-export?site_id=${selectedSite}&start_date=${exportStartDate}&end_date=${exportEndDate}`
        : `${API}/ohs/attendance/export?site_id=${selectedSite}&start_date=${exportStartDate}&end_date=${exportEndDate}`;
      const response = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${isSummary ? "attendance_summary" : "ohs_attendance"}_${exportStartDate}_to_${exportEndDate}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Attendance exported successfully");
      }
    } catch (error) {
      console.error("Error exporting attendance:", error);
      toast.error("Failed to export attendance");
    }
  };

  const generateQRCode = async (bilingual = false) => {
    if (!selectedSite) return;

    // Get site details to show QR code URL is permanent
    const site = sites.find(s => s.id === selectedSite);
    const siteName = site?.name || "Site";
    const checkInUrl = `${window.location.origin}${site?.qr_code_url || `/ohs/check-in/${selectedSite}`}`;

    const CJK_FONT = "'Noto Sans SC', 'Microsoft YaHei', 'PingFang SC', sans-serif";
    const NAVY = "#0D1B2A";
    const GREY = "#64748b";

    try {
      // Ensure the CJK font + glyphs are loaded before drawing (prevents tofu boxes)
      if (bilingual && document.fonts) {
        try {
          const glyphs = QR_NOTES.heading.zh + QR_NOTES.signin.zh + QR_NOTES.help.zh;
          await Promise.all([
            document.fonts.load(`700 26px ${CJK_FONT}`, glyphs),
            document.fonts.load(`500 24px ${CJK_FONT}`, glyphs),
          ]);
          await document.fonts.ready;
        } catch (e) {
          /* fall through — system CJK fonts will be used if available */
        }
      }

      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(checkInUrl, {
        width: 400,
        margin: 2,
      });

      const qrImage = new Image();
      qrImage.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const qrSize = 400;
        const padding = 40;
        const titleHeight = 60;
        const siteNameHeight = 50;
        canvas.width = qrSize + (padding * 2); // 480
        const innerWidth = qrSize; // usable text width

        // Helper: split text into lines that fit innerWidth
        const wrapText = (text, font) => {
          ctx.font = font;
          const words = text.split(' ');
          const lines = [];
          let current = '';
          words.forEach((word) => {
            const test = current ? `${current} ${word}` : word;
            if (ctx.measureText(test).width > innerWidth && current) {
              lines.push(current);
              current = word;
            } else {
              current = test;
            }
          });
          if (current) lines.push(current);
          return lines;
        };

        // Build the note "lines" stacked below the QR (bilingual only)
        const noteLines = [];
        if (bilingual) {
          noteLines.push({ gapBefore: 28 });
          noteLines.push({ text: QR_NOTES.heading.zh, font: `700 26px ${CJK_FONT}`, color: NAVY, lh: 36 });
          noteLines.push({ gapBefore: 16 });
          noteLines.push({ text: QR_NOTES.signin.zh, font: `500 24px ${CJK_FONT}`, color: NAVY, lh: 32 });
          noteLines.push({ text: QR_NOTES.signin.en, font: `400 16px Arial, sans-serif`, color: GREY, lh: 24 });
          noteLines.push({ gapBefore: 18, divider: true });
          wrapText(QR_NOTES.help.zh, `500 22px ${CJK_FONT}`).forEach((ln, i) =>
            noteLines.push({ text: ln, font: `500 22px ${CJK_FONT}`, color: NAVY, lh: 32, gapBefore: i === 0 ? 18 : 0 })
          );
          wrapText(QR_NOTES.help.en, `400 15px Arial, sans-serif`).forEach((ln, i) =>
            noteLines.push({ text: ln, font: `400 15px Arial, sans-serif`, color: GREY, lh: 22, gapBefore: i === 0 ? 6 : 0 })
          );
        }

        // Compute total notes height
        const topHeight = padding + titleHeight + siteNameHeight + qrSize;
        let notesHeight = 0;
        noteLines.forEach((l) => {
          notesHeight += (l.gapBefore || 0) + (l.lh || (l.divider ? 1 : 0));
        });
        canvas.height = topHeight + notesHeight + (bilingual ? 30 : 20);

        // Background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.textAlign = 'center';

        // Title (English heading — unchanged)
        ctx.fillStyle = NAVY;
        ctx.font = 'bold 32px Arial, sans-serif';
        ctx.fillText('OH&S Attendance QR Code', canvas.width / 2, padding + 35);

        // Site name (unchanged, amber/green)
        ctx.fillStyle = '#059669';
        ctx.font = 'bold 24px Arial, sans-serif';
        ctx.fillText(siteName, canvas.width / 2, padding + titleHeight + 30);

        // QR code image
        ctx.drawImage(qrImage, padding, padding + titleHeight + siteNameHeight, qrSize, qrSize);

        // Bilingual notes below the QR
        let y = topHeight;
        noteLines.forEach((l) => {
          y += l.gapBefore || 0;
          if (l.divider) {
            ctx.strokeStyle = '#e2e8f0';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(padding + 40, y);
            ctx.lineTo(canvas.width - padding - 40, y);
            ctx.stroke();
            return;
          }
          if (l.text) {
            y += l.lh;
            ctx.fillStyle = l.color;
            ctx.font = l.font;
            ctx.fillText(l.text, canvas.width / 2, y - 6);
          }
        });

        setQrCodeUrl(canvas.toDataURL('image/png'));
        setShowQRDialog(true);
      };
      qrImage.src = qrDataUrl;

    } catch (error) {
      console.error("Error generating QR code:", error);
      toast.error("Failed to generate QR code");
    }
  };

  const downloadQRCode = () => {
    const siteName = sites.find(s => s.id === selectedSite)?.name || "Site";
    const a = document.createElement("a");
    a.href = qrCodeUrl;
    a.download = `OH&S_Attendance_QR_${siteName.replace(/\s/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("QR code downloaded");
  };

  const printQRCode = () => {
    if (!qrCodeUrl) return;
    const siteName = sites.find(s => s.id === selectedSite)?.name || "Site";
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("Please allow pop-ups to print the QR sheet");
      return;
    }
    w.document.write(`<!DOCTYPE html><html><head><title>OH&S Attendance QR - ${siteName}</title>
      <style>
        @page { size: A4; margin: 12mm; }
        html, body { margin: 0; padding: 0; }
        .wrap { display: flex; align-items: center; justify-content: center; min-height: 100vh; }
        img { width: 100%; max-width: 150mm; height: auto; }
      </style></head>
      <body><div class="wrap"><img src="${qrCodeUrl}" alt="QR Code" /></div>
      <script>
        const img = document.images[0];
        const go = () => { window.focus(); window.print(); };
        if (img.complete) { setTimeout(go, 200); } else { img.onload = () => setTimeout(go, 200); }
        window.onafterprint = () => window.close();
      <\/script></body></html>`);
    w.document.close();
  };

  const handleDeleteAttendance = async (attendanceId, personName) => {
    try {
      const response = await fetch(`${API}/ohs/attendance/${attendanceId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success(`Deleted check-in for ${personName}`);
        // Reload attendance and comparison data
        loadAttendance();
        loadComparison();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete attendance record");
      }
    } catch (error) {
      console.error("Error deleting attendance:", error);
      toast.error("Failed to delete attendance record");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleEditAttendance = (record) => {
    // Extract date from check_in_datetime
    const checkInDate = record.check_in_datetime ? record.check_in_datetime.split('T')[0] : "";
    
    setEditingAttendance({
      id: record.id,
      mobile_number: formatMobileNumber(record.mobile_number || ""),
      first_name: record.first_name,
      surname: record.surname,
      contractor_company: record.contractor_company,
      check_in_date: checkInDate,
    });
    setShowEditAttendanceDialog(true);
  };

  const handleUpdateAttendance = async (e) => {
    e.preventDefault();
    
    // Validate company selection
    if (!editingAttendance.contractor_company) {
      toast.error("Please select a company");
      return;
    }
    
    // Validate mobile number
    if (!editingAttendance.mobile_number || editingAttendance.mobile_number.replace(/\D/g, '').length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    
    // Validate name fields
    if (!editingAttendance.first_name || !editingAttendance.surname) {
      toast.error("Please enter first name and surname");
      return;
    }
    
    try {
      const response = await fetch(`${API}/ohs/attendance/${editingAttendance.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mobile_number: editingAttendance.mobile_number,
          first_name: editingAttendance.first_name,
          surname: editingAttendance.surname,
          contractor_company: editingAttendance.contractor_company,
          check_in_date: editingAttendance.check_in_date,
        }),
      });

      if (response.ok) {
        toast.success("Attendance record updated successfully");
        setShowEditAttendanceDialog(false);
        setEditingAttendance(null);
        loadAttendance();
        loadComparison();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update attendance record");
      }
    } catch (error) {
      console.error("Error updating attendance:", error);
      toast.error("Failed to update attendance record");
    }
  };

  // Handle mobile number formatting and auto-fill (for add dialog)
  const handleMobileNumberChange = async (e) => {
    const formatted = formatMobileNumber(e.target.value);
    setNewAttendance(prev => ({ ...prev, mobile_number: formatted }));

    // Auto-search when 10 digits entered
    const numbers = formatted.replace(/\D/g, "");
    if (numbers.length === 10) {
      try {
        const response = await fetch(`${API}/ohs/personnel/search/${numbers}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setNewAttendance(prev => ({
              ...prev,
              first_name: data.first_name || prev.first_name,
              surname: data.surname || prev.surname,
              contractor_company: data.contractor_company || prev.contractor_company,
            }));
            toast.success("Personnel details found and filled");
          }
        }
      } catch (error) {
        console.error("Error searching personnel:", error);
      }
    }
  };

  // Handle mobile number formatting for edit dialog
  const handleEditMobileNumberChange = (e) => {
    const formatted = formatMobileNumber(e.target.value);
    setEditingAttendance(prev => ({ ...prev, mobile_number: formatted }));
  };

  const handleAddAttendance = async (e) => {
    e.preventDefault();
    
    // Validate company selection
    if (!newAttendance.contractor_company) {
      toast.error("Please select a company");
      return;
    }
    
    // Validate mobile number
    if (!newAttendance.mobile_number || newAttendance.mobile_number.replace(/\D/g, '').length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    
    // Validate name fields
    if (!newAttendance.first_name || !newAttendance.surname) {
      toast.error("Please enter first name and surname");
      return;
    }
    
    try {
      const response = await fetch(`${API}/ohs/check-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...newAttendance,
          site_id: selectedSite,
          check_in_date: selectedDate,
        }),
      });

      if (response.ok) {
        toast.success("Attendance added successfully");
        setShowAddAttendanceDialog(false);
        setNewAttendance({
          mobile_number: "",
          first_name: "",
          surname: "",
          contractor_company: "",
        });
        // Reload attendance and comparison data
        loadAttendance();
        loadComparison();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add attendance");
      }
    } catch (error) {
      console.error("Error adding attendance:", error);
      toast.error("Failed to add attendance");
    }
  };

  // Load site-specific companies when dialog opens
  useEffect(() => {
    const loadSiteCompanies = async () => {
      if ((showAddAttendanceDialog || showEditAttendanceDialog)) {
        try {
          // Load all active contractor/subcontractor companies from the company database
          const response = await fetch(`${API}/companies?active_only=true`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            // Filter to only show contractors and subcontractors
            let contractors = data.filter(c => 
              c.company_type === "contractor" || c.company_type === "subcontractor"
            );
            
            // Filter by site association if a site is selected
            if (selectedSite) {
              contractors = contractors.filter(c => 
                // Show companies with no site associations (global) OR companies associated with this site
                !c.site_ids || c.site_ids.length === 0 || c.site_ids.includes(selectedSite)
              );
            }
            
            // Sort alphabetically by company name
            contractors.sort((a, b) => a.name.localeCompare(b.name));
            setSiteCompanies(contractors);
          }
        } catch (error) {
          console.error("Error loading companies:", error);
        }
      }
    };
    
    loadSiteCompanies();
  }, [showAddAttendanceDialog, showEditAttendanceDialog, selectedSite, token]);

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString("en-AU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const selectedSiteName = sites.find(s => s.id === selectedSite)?.name || "";

  // Filter attendance by selected company
  const filteredAttendance = selectedCompanyFilter === "all" 
    ? attendance 
    : attendance.filter(record => companies[record.contractor_company] === selectedCompanyFilter);

  return (
    <div className="p-2 sm:p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">Daily Site Attendance Tracking</h1>
        <p className="text-sm sm:text-base text-slate-600">Monitor site attendance and safety compliance</p>
      </div>

      {/* Controls */}
      <Card className="mb-4 md:mb-6">
        <CardContent className="pt-4 md:pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
            <div>
              <Label htmlFor="site">Site</Label>
              <Select value={selectedSite} onValueChange={setSelectedSite}>
                <SelectTrigger>
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>

            <div className="flex items-end space-x-2">
              <Button onClick={() => { if (!selectedSite) { toast.error("Please select a site first"); return; } setShowQRLangDialog(true); }} className="flex-1" data-testid="view-qr-code-button">
                <QrCode className="w-4 h-4 mr-2" />
                View QR Code
              </Button>
              <Button 
                onClick={() => setShowAddAttendanceDialog(true)} 
                variant="outline"
                className="flex-1"
              >
                <Users className="w-4 h-4 mr-2" />
                Add Attendance
              </Button>
            </div>
          </div>

          {/* Export Controls */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Export Attendance Report</h3>
            <div className="mb-3 max-w-xs">
              <Label htmlFor="exportType">Report Type</Label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger id="exportType" data-testid="export-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="detailed" data-testid="export-type-detailed">Detailed Attendance (per check-in)</SelectItem>
                  <SelectItem value="summary" data-testid="export-type-summary">Company Summary (On Site vs Checked In)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
              <div>
                <Label htmlFor="exportStartDate">From Date</Label>
                <Input
                  id="exportStartDate"
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="exportEndDate">To Date</Label>
                <Input
                  id="exportEndDate"
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                />
              </div>

              <div className="flex items-end">
                <Button onClick={handleExport} variant="outline" className="w-full" data-testid="export-attendance-button">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV ({exportStartDate} to {exportEndDate})
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Comparison Table */}
      {comparison && (
        <Card className="mb-4 md:mb-6">
          <CardHeader>
            <CardTitle className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <span className="text-lg sm:text-xl">Company Attendance Summary</span>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm font-normal">
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded"></div>
                  <span>On Site</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span>Checked In</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <div className="w-3 h-3 bg-orange-500 rounded"></div>
                  <span>Missing</span>
                </div>
                <div className="flex items-center space-x-1 sm:space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded"></div>
                  <span>Unexpected</span>
                </div>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs sm:text-sm">Company</TableHead>
                    <TableHead className="text-center text-xs sm:text-sm">On Site</TableHead>
                    <TableHead className="text-center text-xs sm:text-sm">Checked In</TableHead>
                    <TableHead className="text-center text-xs sm:text-sm">Missing</TableHead>
                    <TableHead className="text-center text-xs sm:text-sm">Unexpected</TableHead>
                    <TableHead className="text-center text-xs sm:text-sm">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const allCompanies = new Set([
                      ...Object.keys(comparison.companies_comparison.expected || {}),
                      ...Object.keys(comparison.companies_comparison.actual || {})
                    ]);
                    
                    return Array.from(allCompanies).sort().map((companyName) => {
                      const expected = comparison.companies_comparison.expected[companyName] || 0;
                      const actual = comparison.companies_comparison.actual[companyName] || 0;
                      const missing = Math.max(0, expected - actual);
                      const unexpected = Math.max(0, actual - expected);
                      
                      let statusIcon;
                      let statusText;
                      let statusColor;
                      
                      if (expected === 0 && actual > 0) {
                        statusIcon = <AlertTriangle className="w-4 h-4" />;
                        statusText = "Not in Dashboard";
                        statusColor = "text-purple-600";
                      } else if (missing > 0) {
                        statusIcon = <AlertTriangle className="w-4 h-4" />;
                        statusText = `${missing} Missing`;
                        statusColor = "text-orange-600";
                      } else if (unexpected > 0) {
                        statusIcon = <AlertTriangle className="w-4 h-4" />;
                        statusText = `${unexpected} Extra`;
                        statusColor = "text-purple-600";
                      } else if (expected === actual && actual > 0) {
                        statusIcon = <CheckCircle2 className="w-4 h-4" />;
                        statusText = "Complete";
                        statusColor = "text-green-600";
                      } else {
                        statusIcon = <Users className="w-4 h-4" />;
                        statusText = "No Activity";
                        statusColor = "text-slate-400";
                      }
                      
                      return (
                        <TableRow 
                          key={companyName}
                          className={`cursor-pointer transition-colors ${
                            selectedCompanyFilter === companyName 
                              ? 'bg-green-100 hover:bg-green-200 border-l-4 border-l-green-600' 
                              : 'hover:bg-slate-50'
                          }`}
                          onClick={() => setSelectedCompanyFilter(companyName)}
                        >
                          <TableCell className={`font-medium text-xs sm:text-sm ${selectedCompanyFilter === companyName ? 'text-green-900' : ''}`}>
                            {companyName}
                            {selectedCompanyFilter === companyName && (
                              <Badge className="ml-2 bg-green-600 text-white text-xs">SELECTED</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-blue-50 text-xs sm:text-sm">{expected}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-green-50">{actual}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            {missing > 0 ? (
                              <Badge variant="outline" className="bg-orange-50 text-orange-700">{missing}</Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {unexpected > 0 ? (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700">{unexpected}</Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className={`flex items-center justify-center space-x-1 ${statusColor}`}>
                              {statusIcon}
                              <span className="text-sm">{statusText}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()}
                  
                  {/* Totals Row */}
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-blue-600">{comparison.summary.total_expected}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-green-600">{comparison.summary.total_checked_in}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {comparison.summary.missing_count > 0 ? (
                        <Badge className="bg-orange-600">{comparison.summary.missing_count}</Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {comparison.summary.unexpected_count > 0 ? (
                        <Badge className="bg-purple-600">{comparison.summary.unexpected_count}</Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-slate-600 text-sm">Summary</span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            
            <p className="text-xs text-slate-500 mt-3 italic">
              💡 Click on any company row to filter attendance records. Selected company will be highlighted in green.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Alert for missing check-ins */}
      {comparison && comparison.summary.missing_count > 0 && (
        <Card className="mb-6 border-orange-200 bg-orange-50">
          <CardContent className="pt-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-orange-900">Missing Check-Ins Detected</p>
                <p className="text-sm text-orange-800 mt-1">
                  {comparison.summary.missing_count} personnel from the dashboard have not checked in via OH&S system.
                  This may indicate personnel on-site without safety compliance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alert for unexpected check-ins */}
      {comparison && comparison.summary.unexpected_count > 0 && (
        <Card className="mb-6 border-purple-200 bg-purple-50">
          <CardContent className="pt-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-purple-900">Dashboard Update Required</p>
                <p className="text-sm text-purple-800 mt-1">
                  {comparison.summary.unexpected_count} personnel checked in but are not recorded in today's dashboard.
                  <strong> Please update the dashboard with the following:</strong>
                </p>
                <ul className="mt-2 space-y-1">
                  {comparison.unexpected_checkins.map((item, idx) => (
                    <li key={idx} className="text-sm text-purple-800">
                      • <strong>{item.person.first_name} {item.person.surname}</strong> ({item.person.contractor_company}) - {item.reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5" />
              <span>Attendance Records - {new Date(selectedDate).toLocaleDateString("en-AU")}</span>
            </div>
            {selectedCompanyFilter !== "all" && (
              <div className="flex items-center space-x-2">
                <span className="text-sm font-normal text-slate-600">Filtering:</span>
                <Badge className="bg-green-600 text-white">
                  {selectedCompanyFilter}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCompanyFilter("all")}
                  className="h-7"
                >
                  Clear Filter
                </Button>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-600">Loading attendance records...</div>
          ) : filteredAttendance.length === 0 ? (
            <div className="text-center py-8 text-slate-600">
              {selectedCompanyFilter !== "all" 
                ? `No attendance records for ${selectedCompanyFilter} on this date.`
                : "No attendance records for this date. Share the QR code to allow personnel to check in."
              }
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Mobile Number</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAttendance.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>
                        <Badge variant="outline" className="font-medium">
                          {companies[record.contractor_company] || "Unknown"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {record.first_name} {record.surname}
                      </TableCell>
                      <TableCell>{record.mobile_number}</TableCell>
                      <TableCell className="text-slate-600">
                        {new Date(record.check_in_datetime).toLocaleTimeString("en-AU", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </TableCell>
                      <TableCell className="text-center">
                        {user?.role !== "view_only" && (
                          <div className="flex justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAttendance(record)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm({
                                id: record.id,
                                name: `${record.first_name} ${record.surname}`,
                                company: companies[record.contractor_company] || "Unknown",
                                time: new Date(record.check_in_datetime).toLocaleTimeString("en-AU", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              })}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR Language Choice Dialog */}
      <Dialog open={showQRLangDialog} onOpenChange={setShowQRLangDialog}>
        <DialogContent className="max-w-sm" data-testid="qr-lang-dialog">
          <DialogHeader>
            <DialogTitle>QR Sheet Language</DialogTitle>
            <DialogDescription>
              Choose how the printed instructions should appear on the QR sheet for {selectedSiteName}.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col space-y-3 pt-2">
            <Button
              data-testid="qr-lang-bilingual-button"
              onClick={() => { setShowQRLangDialog(false); generateQRCode(true); }}
              className="w-full justify-start h-auto py-3"
            >
              <div className="text-left">
                <div className="font-semibold">Bilingual (English + 中文)</div>
                <div className="text-xs opacity-80 font-normal">Adds Simplified Chinese instructions below the QR</div>
              </div>
            </Button>
            <Button
              data-testid="qr-lang-english-button"
              variant="outline"
              onClick={() => { setShowQRLangDialog(false); generateQRCode(false); }}
              className="w-full justify-start h-auto py-3"
            >
              <div className="text-left">
                <div className="font-semibold">English only</div>
                <div className="text-xs opacity-70 font-normal">Standard sheet (heading + site name)</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>OH&S Check-In QR Code</DialogTitle>
            <DialogDescription>
              Permanent QR code for {selectedSiteName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {qrCodeUrl && (
              <div className="flex flex-col items-center">
                <img src={qrCodeUrl} alt="QR Code" className="w-full max-w-sm" />
                <p className="text-sm text-slate-600 mt-4 text-center">
                  Personnel should scan this code with their mobile device to check in
                </p>
              </div>
            )}

            <div className="flex space-x-2">
              <Button onClick={downloadQRCode} className="flex-1" data-testid="download-qr-button">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button onClick={printQRCode} variant="secondary" className="flex-1" data-testid="print-qr-button">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button onClick={() => setShowQRDialog(false)} variant="outline" className="flex-1">
                Close
              </Button>
            </div>

            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <p className="text-xs text-green-800">
                <strong>✅ Permanent QR Code:</strong> This QR code is unique to this site and will never change. 
                You can print it once and use it indefinitely.
              </p>
            </div>
            
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-800">
                <strong>💡 Tip:</strong> Display this QR code prominently at the site entrance for easy access.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm !== null} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Attendance Record?</DialogTitle>
            <DialogDescription>
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {deleteConfirm && (
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="space-y-2">
                  <p className="text-sm text-red-900">
                    <strong>Person:</strong> {deleteConfirm.name}
                  </p>
                  <p className="text-sm text-red-900">
                    <strong>Company:</strong> {deleteConfirm.company}
                  </p>
                  <p className="text-sm text-red-900">
                    <strong>Check-in Time:</strong> {deleteConfirm.time}
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Only delete this record if the person checked in at the wrong site by mistake. 
                  This will allow them to check in at the correct site.
                </p>
              </div>

              <div className="flex space-x-2">
                <Button
                  onClick={() => handleDeleteAttendance(deleteConfirm.id, deleteConfirm.name)}
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Record
                </Button>
                <Button
                  onClick={() => setDeleteConfirm(null)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Attendance Dialog */}
      <Dialog open={showAddAttendanceDialog} onOpenChange={setShowAddAttendanceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Attendance Manually</DialogTitle>
            <DialogDescription>
              Add a personnel check-in for {selectedSiteName} on {selectedDate}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddAttendance} className="space-y-4">
            <div>
              <Label htmlFor="add_mobile">Mobile Number *</Label>
              <Input
                id="add_mobile"
                type="tel"
                placeholder="0412 345 678"
                value={newAttendance.mobile_number}
                onChange={handleMobileNumberChange}
                maxLength={12}
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Format: #### ### ### (auto-fills details if found)
              </p>
            </div>

            <div>
              <Label htmlFor="add_first_name">First Name *</Label>
              <Input
                id="add_first_name"
                value={newAttendance.first_name}
                onChange={(e) => setNewAttendance(prev => ({ ...prev, first_name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="add_surname">Surname *</Label>
              <Input
                id="add_surname"
                value={newAttendance.surname}
                onChange={(e) => setNewAttendance(prev => ({ ...prev, surname: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="add_company">Contractor Company *</Label>
              <Select
                value={newAttendance.contractor_company}
                onValueChange={(value) => setNewAttendance(prev => ({ ...prev, contractor_company: value }))}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {siteCompanies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1">
                Add Attendance
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowAddAttendanceDialog(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Attendance Dialog */}
      <Dialog open={showEditAttendanceDialog} onOpenChange={setShowEditAttendanceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Attendance Record</DialogTitle>
            <DialogDescription>
              Update attendance details for {editingAttendance?.first_name} {editingAttendance?.surname}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleUpdateAttendance} className="space-y-4">
            <div>
              <Label htmlFor="edit_date">Check-In Date *</Label>
              <Input
                id="edit_date"
                type="date"
                value={editingAttendance?.check_in_date || ""}
                onChange={(e) => setEditingAttendance(prev => ({ ...prev, check_in_date: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_mobile">Mobile Number *</Label>
              <Input
                id="edit_mobile"
                type="tel"
                placeholder="0412 345 678"
                value={editingAttendance?.mobile_number || ""}
                onChange={handleEditMobileNumberChange}
                maxLength={12}
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                Format: #### ### ###
              </p>
            </div>

            <div>
              <Label htmlFor="edit_first_name">First Name *</Label>
              <Input
                id="edit_first_name"
                value={editingAttendance?.first_name || ""}
                onChange={(e) => setEditingAttendance(prev => ({ ...prev, first_name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_surname">Surname *</Label>
              <Input
                id="edit_surname"
                value={editingAttendance?.surname || ""}
                onChange={(e) => setEditingAttendance(prev => ({ ...prev, surname: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label htmlFor="edit_company">Contractor Company *</Label>
              <Select
                value={editingAttendance?.contractor_company || ""}
                onValueChange={(value) => setEditingAttendance(prev => ({ ...prev, contractor_company: value }))}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {siteCompanies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex space-x-2 pt-4">
              <Button type="submit" className="flex-1">
                Update Attendance
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setShowEditAttendanceDialog(false);
                  setEditingAttendance(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
